import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { FileText, Loader2, ImageIcon, FileQuestion } from "lucide-react";

export default function DocumentViewer({ documents }) {
  const [selected, setSelected] = useState(documents[0]?.id || "");
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (documents.length && !documents.find((d) => d.id === selected)) {
      setSelected(documents[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  const doc = documents.find((d) => d.id === selected);
  const ctype = (doc?.content_type || "").toLowerCase();
  const isPdf = ctype.includes("pdf");
  const isImage = ctype.startsWith("image/");

  useEffect(() => {
    let current;
    const load = async () => {
      if (!doc) { setBlobUrl(""); return; }
      setLoading(true);
      try {
        const res = await api.get(`/files/${doc.storage_path}`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        current = url;
        setBlobUrl(url);
      } catch (e) {
        setBlobUrl("");
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => { if (current) URL.revokeObjectURL(current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, doc?.storage_path]);

  return (
    <div className="h-full flex flex-col border border-slate-800 rounded-xl bg-slate-900 text-white overflow-hidden shadow-sm">
      <div className="p-3 border-b border-slate-800 bg-slate-950">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-2">Documento</p>
        {documents.length === 0 ? (
          <p className="text-sm text-slate-400">Nenhum documento enviado.</p>
        ) : (
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="doc-viewer-select" className="bg-slate-800 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {documents.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.doc_type} — {d.original_filename}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="flex-1 overflow-auto bg-slate-800 flex items-center justify-center" data-testid="doc-viewer-preview">
        {loading ? (
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        ) : !doc ? (
          <div className="text-center text-slate-500">
            <FileText className="h-10 w-10 mx-auto mb-2" />
            <p className="text-sm">Selecione um documento para visualizar.</p>
          </div>
        ) : isPdf && blobUrl ? (
          <iframe title="Documento" src={blobUrl} className="w-full h-full min-h-[500px] bg-white" />
        ) : isImage && blobUrl ? (
          <img src={blobUrl} alt={doc.original_filename} className="max-w-full max-h-full object-contain" />
        ) : (
          <div className="text-center text-slate-400 p-6">
            {isImage ? <ImageIcon className="h-10 w-10 mx-auto mb-2" /> : <FileQuestion className="h-10 w-10 mx-auto mb-2" />}
            <p className="text-sm">Pré-visualização não disponível para este tipo.</p>
            {blobUrl && <a href={blobUrl} download={doc.original_filename} className="text-amber-400 underline text-sm mt-2 inline-block">Baixar arquivo</a>}
          </div>
        )}
      </div>
    </div>
  );
}
